import api from './api';

/**
 * Liquidação de bilheteira ao promotor.
 *
 * Fala com o módulo /v1/pass/settlements (pass-settlements.ts), que apura a partir
 * da comissão gravada por bilhete (pg_ticket_commission) — a MESMA fonte que a
 * Faturação do promotor. Aqui o admin corre o período, vê o líquido a pagar, e
 * aprova+paga com referência bancária.
 */

export interface Liquidacao {
  id: string;
  promoter_entity_id: string;
  settlement_type: string;
  period_start: string;
  period_end: string;
  gross_ticketing_cve: number;
  commission_cve: number;
  vat_on_commission_cve: number;
  net_payable_cve: number;
  /** Taxa SISP cobrada à parte (contrato PASS_THROUGH): valor real da Reconciliação SISP. */
  sisp_fee_cve?: number;
  sisp_fee_vat_cve?: number;
  sisp_fee_trx?: number | null;
  /** Pedidos da liquidação sem transacção SISP casada — a taxa deles não entrou. */
  sisp_orders_unmatched?: number | null;
  status: 'DRAFT' | 'PAID' | string;
  bank_reference?: string | null;
  paid_at?: string | null;
  created_at?: string;
  /** Custo real SISP destas transacções (só admin). A margem é sisp_fee_cve − isto. */
  sisp_cost_cve?: number | null;
  /** Taxa SISP discriminada por cartão (custoCVE só vem ao admin). */
  sisp_detail?: {
    porCartao?: Record<string, { trx: number; valorCVE: number; feeCVE: number; custoCVE?: number; taxaBps: number | null }>;
    porMeio?: Record<string, { trx: number; valorCVE: number; feeCVE: number; custoCVE?: number; taxaBps: number | null }>;
    pedidosSemSisp?: Array<number | string>;
  } | string | null;
  /** Bilhetes desta liquidação. */
  bilhetes?: number | null;
  /** Liquidação de um só evento (NULL nas liquidações por período). */
  event_id?: number | null;
  evento_titulo?: string | null;
}

/** Um evento do promotor: o que vendeu e em que fase está a sua liquidação. */
export interface EventoLiquidacao {
  eventId: number;
  titulo: string;
  dataInicio: string | null;
  vendidos: number;
  receita: number;
  /** Bilhetes vendidos sem comissão gravada — ficam fora da liquidação. */
  semComissao: number;
  pendente: {
    bilhetes: number; bruto: number; comissao: number; iva: number; liquido: number;
    /** Taxa SISP do pendente: o que a SISP cobrou e o que a Pagali cobra (contrato). */
    sispCusto?: number; sispCobrado?: number; sispIva?: number; semSisp?: number;
  };
  /** O evento inteiro (por liquidar + rascunho + pago), pelo contrato em vigor. */
  total?: {
    bilhetes: number; bruto: number; comissao: number; iva: number;
    sispCusto: number; sispCobrado: number; sispIva: number; semSisp: number; liquido: number;
    /** Por meio de pagamento: VINTI4, CARTAO_GUARDADO, INTERNACIONAL. */
    porMeio?: Record<string, { trx: number; valorCVE: number; feeCVE: number; custoCVE?: number; taxaBps: number | null }>;
  };
  rascunho: { bilhetes: number; bruto: number };
  pago: { bilhetes: number; bruto: number };
}

/** O contrato em vigor que a liquidação aplica. */
export interface ContratoLiquidacao {
  id: string; version: number; effectiveFrom: string;
  escaloes: Array<{ maxTickets: number | null; rateBps: number }>;
  tectoCVE: number | null; ivaBps: number;
  sisp: { modo: 'INCLUDED' | 'PASS_THROUGH' | string; ivaSobreTaxa: boolean; vinti4Bps: number | null; intlBps: number | null };
}

export const liquidacoesService = {
  /** Lista as liquidações de um promotor (mais recentes primeiro, como vier). */
  async listar(promoterEntityId: string): Promise<Liquidacao[]> {
    const { data } = await api.get(
      `/pass/settlements/${encodeURIComponent(promoterEntityId)}`,
    );
    return (data?.settlements ?? []) as Liquidacao[];
  },

  /** Corre a liquidação de bilheteira de um período → cria uma liquidação DRAFT. */
  async correrBilheteira(
    promoterEntityId: string,
    periodStart: string,
    periodEnd: string,
  ): Promise<string> {
    const { data } = await api.post('/pass/settlements/ticketing', {
      promoterEntityId,
      periodStart,
      periodEnd,
    });
    return data?.settlementId as string;
  },

  /** Resumo por evento do promotor (super-admin). */
  async eventos(promoterEntityId: string): Promise<{ eventos: EventoLiquidacao[]; contrato: ContratoLiquidacao | null }> {
    const { data } = await api.get(
      `/pass/settlements/${encodeURIComponent(promoterEntityId)}/events`,
    );
    return { eventos: (data?.eventos ?? []) as EventoLiquidacao[], contrato: data?.contrato ?? null };
  },

  /** Liquida tudo o que um evento tem por liquidar → cria uma liquidação DRAFT. */
  async correrEvento(promoterEntityId: string, eventId: number): Promise<string> {
    const { data } = await api.post('/pass/settlements/ticketing', { promoterEntityId, eventId });
    return data?.settlementId as string;
  },

  /** Aprova e marca como PAGA (super-admin), com referência da transferência. */
  async aprovarEPagar(settlementId: string, bankReference: string): Promise<void> {
    await api.patch(
      `/pass/settlements/${encodeURIComponent(settlementId)}/approve`,
      { bankReference },
    );
  },

  /** Anula uma liquidação em DRAFT (super-admin). */
  async anular(settlementId: string): Promise<void> {
    await api.delete(`/pass/settlements/${encodeURIComponent(settlementId)}`);
  },
};

export default liquidacoesService;
